import React, { useCallback, useEffect, useRef, useState } from "react";

import { faExpand } from "@fortawesome/pro-regular-svg-icons";
import { Controller, useForm, useFormContext } from "react-hook-form";
import { twJoin } from "tailwind-merge";

import { CopyTextIcon } from "src/base-components/CopyTextIcon";
import {
  FloatingWindowInternal,
  useFloatingWindowPosition,
} from "src/base-components/FloatingWindow/FloatingWindow";
import {
  useFloatingWindowsActions,
  useIsFloatingWindowOpen,
} from "src/base-components/FloatingWindow/hooks";
import {
  FloatingWindowProps,
  PROMPT_WINDOW_ID,
} from "src/base-components/FloatingWindow/store";
import { TextEditor } from "src/base-components/TextEditor/TextEditor";
import { Toolbar } from "src/base-components/TextEditor/Toolbar";
import { AgentVersionConfigurationInput } from "src/clients/agent-management";
import { ErrorHint } from "src/design-system/ErrorHint";
import { IconButton } from "src/design-system/Icon";
import { Tooltip } from "src/design-system/Tooltip";

type AgentPromptEditorProps = {
  immutable: boolean;
  dataLoc?: string;
};

type PromptFormValues = {
  systemPrompt: string;
};

const MAX_PROMPT_LENGTH = 100_000;

const PROMPT_EDITOR_PROPS = {
  placeholder: "You are a helpful AI assistant",
  fullHeight: true,
  keepEmptyLines: true,
  markdownAwareClipboard: true,
  // Drop headings from the ignore list so `#` / `##` shortcuts create real
  // heading blocks (matches the heading toolbar button exposed on the prompt
  // editor).
  ignoreMarkdownTags: ["blockquote", "checkbox"],
} as const;

type PromptEditorWindowData = {
  value: string;
  onSave: (value: string) => void;
  readOnly: boolean;
};

export const AgentPromptEditor: React.FC<AgentPromptEditorProps> = ({
  immutable,
  dataLoc,
}) => {
  const parentForm = useFormContext<AgentVersionConfigurationInput>();

  const localForm = useForm<PromptFormValues>({
    defaultValues: {
      systemPrompt: parentForm.getValues("model_config.system_prompt"),
    },
    mode: "onChange",
  });

  // Sync local → parent (validation-gated)
  useEffect(
    () =>
      localForm.subscribe({
        formState: { values: true },
        callback: async () => {
          const isValid = await localForm.trigger();
          if (!isValid) return;

          const { systemPrompt } = localForm.getValues();
          parentForm.setValue("model_config.system_prompt", systemPrompt, {
            shouldDirty: true,
          });
        },
      }),
    [localForm, parentForm],
  );

  const toolbarRef = useRef<HTMLUListElement>(null);

  const { open: openWindow } = useFloatingWindowsActions();
  const isWindowOpen = useIsFloatingWindowOpen(PROMPT_WINDOW_ID);

  const handleOpenExpanded = useCallback(() => {
    if (isWindowOpen) return;

    openWindow(
      { id: PROMPT_WINDOW_ID, type: "prompt-editor", target: undefined },
      {
        value: localForm.getValues("systemPrompt"),
        onSave: (newValue: string) => {
          localForm.setValue("systemPrompt", newValue, {
            shouldValidate: true,
            shouldDirty: true,
          });
        },
        readOnly: immutable,
      } satisfies PromptEditorWindowData,
    );
  }, [isWindowOpen, openWindow, localForm, immutable]);

  return (
    <div data-loc={dataLoc}>
      <div className="mb-2 flex items-center justify-between">
        <label className="font-inter-semibold-13px text-gray-800">Prompt</label>
        <div className="flex gap-1.5">
          <CopyTextIcon
            feedback="inline-only-checkmark"
            size="xs"
            tooltip="Copy"
            value={() => localForm.getValues("systemPrompt")}
          />
          <Tooltip placement="top" title="Expand" asChild>
            <IconButton
              dataLoc="expand-prompt-button"
              icon={faExpand}
              size="xs"
              onClick={handleOpenExpanded}
            />
          </Tooltip>
        </div>
      </div>
      <Controller
        control={localForm.control}
        name="systemPrompt"
        render={({ field, fieldState }) => (
          <>
            {fieldState.error && (
              <ErrorHint>{fieldState.error.message}</ErrorHint>
            )}
            <div
              className={twJoin(
                "flex h-90 flex-col rounded-lg border bg-white",
                fieldState.error ? "border-red-600" : "border-gray-200",
                isWindowOpen && "pointer-events-none opacity-50",
                immutable && !isWindowOpen && "opacity-50",
              )}
              data-loc="prompt-editor-container"
            >
              {/* Editor */}
              <div
                className={twJoin(
                  "flex-1 overflow-hidden px-3 pt-3",
                  // Override the global `.ql-editor[contenteditable="false"] { overflow: hidden }`
                  // rule so the read-only prompt remains scrollable.
                  immutable && "[&_.ql-editor]:!overflow-y-auto",
                )}
              >
                <TextEditor
                  {...PROMPT_EDITOR_PROPS}
                  readOnly={immutable || isWindowOpen}
                  toolbarRef={toolbarRef}
                  value={field.value}
                  onChange={field.onChange}
                />
              </div>

              {/* Toolbar — visually hidden when immutable,
                  kept in DOM so Quill retains its toolbar reference */}
              <div
                className={twJoin(
                  "gap-x-3 border-gray-100 border-t px-3 py-2",
                  immutable ? "hidden" : "flex",
                )}
                data-loc="prompt-toolbar"
              >
                <Toolbar ref={toolbarRef} faded={false} />
              </div>
            </div>
          </>
        )}
        rules={{
          maxLength: {
            value: MAX_PROMPT_LENGTH,
            message: `Character limit exceeded. Maximum allowed is ${MAX_PROMPT_LENGTH.toLocaleString()} characters.`,
          },
        }}
      />
    </div>
  );
};

export const PromptExpandedWindow: React.FC<FloatingWindowProps> = ({
  computePosition,
  id,
  data,
}) => {
  const { close: closeWindow } = useFloatingWindowsActions();
  const { position, windowRef } = useFloatingWindowPosition(computePosition);

  const typedData = data as PromptEditorWindowData;
  const toolbarRef = useRef<HTMLUListElement>(null);
  const [localValue, setLocalValue] = useState(typedData.value);

  const handleClose = useCallback(() => {
    typedData.onSave(localValue);
    closeWindow(id);
  }, [typedData, closeWindow, id, localValue]);

  return (
    <FloatingWindowInternal
      ref={windowRef}
      height="h-120"
      id={id}
      style={{
        inset: "0px auto auto 0px",
        transform: position,
      }}
      title="Prompt"
      width="w-160"
      draggable
      isMaximized
      isPinned
      maximizable
      resizeable
      onClose={handleClose}
    >
      <div className="flex h-full flex-col p-2">
        <div
          className={twJoin(
            "flex-1 overflow-hidden",
            // Override the global `.ql-editor[contenteditable="false"] { overflow: hidden }`
            // rule so the read-only prompt remains scrollable.
            typedData.readOnly && "[&_.ql-editor]:!overflow-y-auto",
          )}
        >
          <TextEditor
            {...PROMPT_EDITOR_PROPS}
            maxHeight="max-h-full"
            readOnly={typedData.readOnly}
            toolbarRef={toolbarRef}
            value={localValue}
            onChange={setLocalValue}
          />
        </div>
        {!typedData.readOnly && (
          <div
            className="flex gap-x-3 border-gray-100 border-t pt-2 pl-10"
            data-loc="prompt-expanded-toolbar"
          >
            <Toolbar ref={toolbarRef} faded={false} />
          </div>
        )}
      </div>
    </FloatingWindowInternal>
  );
};
